import { useId, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

/**
 * Every address that leads nowhere, drawn one way (I-0776): no page at it (`NoPageHere`), no work
 * area at it (`WorkAreaRouteNotFound`), and no item at it (`WorkItemDetail`, I-0794). A person meets
 * all three for the same kind of mistake, so they have one layout: the title and why as a heading
 * and a line under it, then the next step. The same card the other address states use
 * (`WorkAreaRouteStates`).
 *
 * `placement="panel"` is the card filling a detail panel beside a page that has its own h1 — a
 * missing item beside its work area's list: the heading is the panel's h2, and the card is drawn on
 * the page's colour, since the panel is already the card's grey.
 *
 * `data-address-not-found` is what `AddressNotFound.test.tsx` requires of every not-found state.
 */
export function AddressNotFound({
  title,
  description,
  next,
  placement = 'page',
}: {
  title: string;
  description: ReactNode;
  /** The way on from here, named for where it leads. */
  next: { to: string; label: string; icon: ReactNode };
  placement?: 'page' | 'panel';
}) {
  const headingId = useId();
  const Heading = placement === 'page' ? 'h1' : 'h2';
  const surface =
    placement === 'page' ? 'bg-[#F1F2F2] dark:bg-[#171717]' : 'bg-[#FEFEFE] dark:bg-[#212121]';
  // A section inside the layout's main landmark, never a second one (I-0703).
  return (
    <section
      aria-labelledby={headingId}
      data-address-not-found=""
      className="flex-1 overflow-y-auto px-5 py-8"
    >
      <div className="mx-auto max-w-xl space-y-4">
        <div className={`rounded-lg px-4 py-4 ${surface}`}>
          <Heading
            id={headingId}
            className="text-[15px] font-semibold text-gray-900 dark:text-gray-100"
          >
            {title}
          </Heading>
          <p className="mt-1 break-words text-[13px] leading-5 text-gray-500 dark:text-gray-400">
            {description}
          </p>
        </div>
        {/* I-1121: the label names a work area, and a long one is a single unbroken run. The link
            never grows past the column; the name breaks where it must, under its icon's line. */}
        <Link
          to={next.to}
          className="inline-flex min-h-touch max-w-full items-center gap-2 text-[13px] font-medium evx-link [&>svg]:shrink-0"
        >
          {next.icon}
          <span className="min-w-0 [overflow-wrap:anywhere]">{next.label}</span>
        </Link>
      </div>
    </section>
  );
}
